import React, { useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import { toast } from "react-hot-toast";
import { withRouter } from "react-router-dom";
import {
  getApplicationData,
  getCountries,
  getMinimumData,
  salesAppPost,
  saveCbdAddendum,
  saveVapeAttestationAddendum,
  saveTravelAddendum,
  sendApplicationLinkToMerchant,
} from "../../../../../api";
import { conditionalAddendums } from "../../../../../constants";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import Spinner from "../../../../components/Loaders/Spinner";
import useApplicationStore from "../../../../stores/application";
import useCommonStore from "../../../../stores/common";
import useSalesFormStore, {
  fetchMccLowRisk,
} from "../../../../stores/sales-form";
import useUserStore from "../../../../stores/user";
import useDomainStore from "../../../../stores/domain";
import { LeadProgression } from "../../../../../constants";
import { useEMSValidation } from "../../../../hooks/useEMSValidation";
import { isDisputeRecoveryVisible } from "../../../../utils/is-dispute-recovery-visible";
import * as Yup from "yup";
import {
  businessInformationSchema,
  businessProfileSchema,
  cbdAddendumSchema,
  creditCardProcessingSchema,
  formSchema,
  paylianceIndustrySchema,
  personalInformationSchema,
  travelAddendumSchema,
  vapeAttestationSchema,
} from "../../validation-schema";
import SendOtp from "../merchant-verification/send-otp";
import VerifyOtp from "../merchant-verification/verify-otp";
import ApplicationStepper from "./components/application-stepper";
import BusinessInformation from "./components/business-information";
import BusinessProfile from "./components/business-profile";
import CreditCardProcessing from "./components/credit-card-processing";
import FileUpload from "./components/file-upload";
import PersonalInformation from "./components/personal-information";
import ReviewSubmit from "./components/review-submit";
import SendToMerchantModal from "./components/send-to-merchant-modal";
import SignInPersonModal from "./components/sign-in-person-modal";
import ThankYou from "./components/thank-you";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import { scrollToError } from "../../utils/scroll-to-error";
import { skipBlankFields } from "../../utils/validation";
import styles from "./style.module.scss";
import ApplicationStepperMobile from "./components/application-stepper/mobile";
import CbdAddendum from "./components/conditional-addendums/cbd-addendum";
import VapeAttestation from "./components/conditional-addendums/vape-attestation";
import TravelAddendum from "./components/conditional-addendums/travel-addendum";
import config from "../../../../../config";
import ArchiveApp from "../../../application/components/ArchiveApp/ArchiveApp";
import { isAppArchived } from "../../../../utils/is-app-archived";
const MAIN_URL = config.paymentcloud.MAIN_URL;

const TRAVEL_SEASONALITY_OPTION_KEYS = ["pct_annual_sales", "max_monthly_card_sales", "min_monthly_card_sales", "monthly_total_sales"];
const TRAVEL_SEASONALITY_MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const COMPLETION_STEPS = [0, 1, 2, 3];

const getTravelSeasonalityFields = () => {
  const result = {};
  TRAVEL_SEASONALITY_OPTION_KEYS.forEach((optionKey) => {
    TRAVEL_SEASONALITY_MONTHS.forEach((month) => {
      result[`travel_seasonality_${optionKey}_${month}`] = "";
    });
  });
  return result;
};

const formatDate = (dateString) => {
  if (!dateString) return dateString;
  const [datePart] = dateString.split(" ");
  const [year, month, day] = datePart.split("-");
  return `${month}/${day}/${year}`;
};

/**
 * Application Form Component
 * Handles the multi-step application process for salesAppPostonboarding
 *
 * @param {Object} props - Component props
 * @param {Object} props.history - React Router history object
 * @param {Object} props.location - React Router location object
 * @param {boolean} [props.isMerchantFlow=false] - Flag to determine if the form is in merchant flow
 * @param {Function} [props.onHideBreadcrumbs] - Callback to hide breadcrumbs in parent component
 */
const ApplicationForm = ({
  history,
  location,
  isMerchantFlow = false,
  match,
  onHideBreadcrumbs,
  ...props
}) => {
  const {
    validateFormPhones,
    validateDrivingLicense,
    validatePhone,
    validateRoutingNumber,
  } = useEMSValidation();
  const { isKurvDomain } = useDomainStore();
  const [isSendToMerchantModalOpen, setIsSendToMerchantModalOpen] =
    useState(false);
  const [openArchiveApp, setOpenArchiveApp] = useState(false);
  const [isArchivedApp, setIsArchivedApp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pricingModel, setPricingModel] = useState(null);
  const [pricingData, setPricingData] = useState([]);
  const [equipmentData, setEquipmentData] = useState([]);
  const [activeAppData, setActiveAppData] = useState(null);
  const [step, setStep] = useState(0);
  const [guestStepSource, setGuestStepSource] = useState(null);
  const [sendingLinkToMerchant, setSendingLinkToMerchant] = useState(false);
  const [showSendToMerchantConfirm, setShowSendToMerchantConfirm] =
    useState(false);

  const [formData, setFormData] = useState({
    // Business Information
    type_of_ownership: "",
    legal_business_name: "",
    dba_name: "",
    tax_ein: "",
    premise_type: "",
    ownership_years: 0,
    ownership_months: 0,
    business_type: "",
    payliance_industry: "",
    tax_exempt: "no",
    business_phone: "",
    business_email: "",
    business_website: "",
    dba_address: "",
    dba_city: "",
    dba_state: "",
    dba_zip: "",
    location_years: 0,
    location_months: 0,
    same_as_dba: "no",
    legal_address: "",
    legal_city: "",
    legal_state: "",
    legal_zip: "",
    legal_phone: "",

    // Personal Information
    personal_same_as_dba_legal: "no",
    title: "",
    personal_first_name: "",
    personal_last_name: "",
    personal_email: "",
    personal_phone: "",
    field_personalstreet: "",
    field_personalcity: "",
    field_personalstate: "",
    field_personalzip: "",
    field_residenceyears: "",
    field_residencemonths: "",
    field_documenttype: "",
    field_documentnumber: "",
    document_country: "",
    field_driverstate: "",
    field_birthdate: "",
    field_ownershippercentage: "",
    field_socialsecurity: "",
    additional_owners: [],

    // Business Profile
    product_service_description: "",
    prod_advertise_description: ["online"],
    is_thirdparty_involved: "no",
    thirdparty_involved_description: "",
    pci_complaint: "no",
    terminated_as_vmdax: "no",
    prior_data_compromise_investigation: "no",
    identified_visa_risk_program: "no",
    visa_risk_program_desc: "",
    is_multiple_locations: "no",
    multi_merchant: "",
    multi_merchant_of: "",
    additional_location: "",
    additional_location_of: "",
    dispute_recovery_program: true,

    // Files uploaded
    files: [],

    // Credit Card Processing
    currently_accept_vdmax: "yes",
    cc_processing_locked: false,
    ways_accepting_cards: [],

    online_sales: "",
    mail_order: "",
    on_premise_sale: "",
    telephone_order: "",
    off_premise_sale: "",

    sales_keyed: "",
    sales_swiped: "",
    recurring_bill_triel_radio: "no",

    monthly_max_sales: "",
    avg_sale_amount: "",
    high_sale_amount: "",
    bank_routing: "",
    bank_account: "",
    bank_name: "",
    business_funding_required: false,
    cheque_processing: "no",
    fraud_chargeback_tool: "no",

    // Equipment
    ems_boot_equipments: [],

    // Conditional Addendums
    cbd_addendum_file: null,
    vape_attestation_what_is_your_source_of_marketing: "",
    vape_attestation_what_audience_is_the_product_marketed_to: "",
    vape_attestation_product_1: "",
    vape_attestation_product_2: "",
    vape_attestation_product_3: "",
    vape_attestation_product_4: "",
    vape_attestation_product_5: "",
    where_are_the_products_manufactured: "",
    vape_attestation_product_types: [{ name: "" }],
    vape_attestation_method_of_age_verification: "",
    vape_attestation_other_method_of_age_verification: "",

    // Travel Addendum
    travel_business_activities: [],
    travel_other_business_activity: "",
    travel_sales_third_party_packages: "",
    travel_agency_accommodation_only: "",
    travel_agency_non_flight_transport: "",
    travel_flight_own_merchant: "",
    travel_flight_airline_merchant: "",
    travel_other_sales_agent: "",
    travel_other_sales_description: "",
    travel_total_gross_turnover: "",
    travel_airline_suppliers: [{ name: "", country: "", payment_terms: "", flight_spend_share: "", average_package_cost: "" }],
    travel_total_spend_travel: "",
    travel_hotel_suppliers: [{ name: "", country: "", payment_terms: "", hotel_spend_share: "", average_package_cost: "" }],
    travel_total_spend_accommodation: "",
    travel_local_services: [{ name: "", country: "", payment_terms: "", service_spend_share: "", average_package_cost: "" }],
    travel_total_spend_other: "",
    travel_countries: [{ name: "", trade_share: "" }],
    ...getTravelSeasonalityFields(),
    travel_average_deposit_percentage: "",
    travel_average_time_deposit_travel: "",
    travel_average_time_balance_travel: "",
    travel_delivery_1_7_days: "",
    travel_delivery_8_14_days: "",
    travel_delivery_15_30_days: "",
    travel_delivery_31_60_days: "",
    travel_delivery_61_90_days: "",
    travel_delivery_91_120_days: "",
    travel_delivery_121_150_days: "",
    travel_delivery_151_180_days: "",
    travel_delivery_over_180_days: "",
    travel_consumer_protection: "",
    travel_website_address: "",
  });

  let { agent, app_slug } = match.params;

  // const editslug = useSalesFormStore((state) => state.editSlug);
  const setApplicationData = useSalesFormStore(
    (state) => state.setApplicationData,
  );
  const setMccOptions = useSalesFormStore((state) => state.setMccOptions);
  const applicationId = useSalesFormStore((state) => state.applicationData?.id);
  const isOtpSent = useSalesFormStore((state) => state.isOtpSent);
  const isAuthenticated = useSalesFormStore((state) => state.isAuthenticated);
  const setIsAuthenticated = useSalesFormStore(
    (state) => state.setIsAuthenticated,
  );
  const otpCode = useSalesFormStore((state) => state.otpCode);
  const user = useUserStore((state) => state.user);
  const isGodUser = useUserStore((state) => state.isGodUser);
  const setEditSlug = useSalesFormStore((state) => state.setEditSlug);
  const setCountryList = useSalesFormStore((state) => state.setCountryList);
  const setMinimumData = useSalesFormStore((state) => state.setMinimumData);
  const minimumData = useSalesFormStore((state) => state.minimumData);
  const setIsSignInPersonModalOpen = useSalesFormStore(
    (state) => state.setIsSignInPersonModalOpen,
  );
  const isSignInPersonModalOpen = useSalesFormStore(
    (state) => state.isSignInPersonModalOpen,
  );
  const otpVerificationTime = useSalesFormStore(
    (state) => state.otpVerificationTime,
  );
  const signerType = useSalesFormStore((state) => state.signerType);
  const setUploadedFiles = useSalesFormStore((state) => state.setUploadedFiles);
  const setIsFinicityConnected = useSalesFormStore(
    (state) => state.setIsFinicityConnected,
  );
  const setHasFinicityRoutingAndAccountNumber = useSalesFormStore(
    (state) => state.setHasFinicityRoutingAndAccountNumber,
  );
  const setIsPlaidConnected = useSalesFormStore(
    (state) => state.setIsPlaidConnected,
  );
  const setHasPlaidRoutingAndAccountNumber = useSalesFormStore(
    (state) => state.setHasPlaidRoutingAndAccountNumber,
  );
  const hasFinicityRoutingAndAccountNumber = useSalesFormStore(
    (state) => state.hasFinicityRoutingAndAccountNumber,
  );
  const hasPlaidRoutingAndAccountNumber = useSalesFormStore(
    (state) => state.hasPlaidRoutingAndAccountNumber,
  );

  const [
    isFirstTimeAppOpenedWithMagicLink,
    setIsFirstTimeAppOpenedWithMagicLink,
  ] = useState(false);

  const disputeRecoveryVisible = isDisputeRecoveryVisible(
    activeAppData?.organization_id ?? user?.organization_id,
  );

  const { cbdAddendumRequired, vapeAttestationRequired, travelAddendumRequired } = useMemo(() => {
    if (!isGodUser) {
      return { cbdAddendumRequired: false, vapeAttestationRequired: false, travelAddendumRequired: false };
    }
    const overrideBank = activeAppData?.override_bank || [];
    const overrideAddendums = activeAppData?.override_addendums || [];
    const hasKurvBank = overrideBank.some((bank) => bank?.value === "Kurv");
    return {
      cbdAddendumRequired: hasKurvBank && overrideAddendums.includes(conditionalAddendums.CBD_ADDENDUM),
      vapeAttestationRequired: hasKurvBank && overrideAddendums.includes(conditionalAddendums.VAPE_ATTESTATION),
      travelAddendumRequired: hasKurvBank && overrideAddendums.includes(conditionalAddendums.TRAVEL_ADDENDUM),
    };
  }, [isGodUser, activeAppData?.override_bank, activeAppData?.override_addendums]);

  const hasPaylianceProcessor = useMemo(
    () =>
      (activeAppData?.override_bank || []).some(
        (bank) => bank?.label === "Payliance",
      ),
    [activeAppData?.override_bank],
  );

  const handleArchiveClose = () => {
    setOpenArchiveApp(false);
    history.push(`/v2/applications`);
  };

  /**
   * Fetches application data and MCC options on component mount
   */
  useEffect(() => {
    if (app_slug) {
      setEditSlug(app_slug);
      getMinimumData(app_slug).then((res) => {
        if (res?.data?.app_version == null) {
          window.location.href = `${MAIN_URL}${res.data.agent_url}/${app_slug}`;
        }
        setMinimumData(res.data);
        setIsFirstTimeAppOpenedWithMagicLink(
          res?.data?.isFirstTimeAppOpened || false,
        );
      });
    }
  }, [app_slug]);

  const setSignerStep = (minimumData, progressionId) => {
    // Additional-signature requests reuse the same sign step as the main flow.
    const effectiveStep =
      progressionId === LeadProgression.ADDITIONAL_SIGNATURE_REQUESTED
        ? 7
        : progressionId;
    if (
      minimumData?.signers?.length > 1 &&
      effectiveStep <= 7 &&
      user == null
    ) {
      let signer = null;
      if (signerType === "First") {
        signer = minimumData?.signers[0];
      } else {
        signer = minimumData?.signers[1];
      }
      if (signer?.status === "signed") {
        setStep(8);
      } else {
        setStep(effectiveStep);
      }
    } else {
      setStep(effectiveStep);
    }
  };

  // keepStep: leave the step as the caller set it rather than rerouting by progression
  const fetchApplicationData = async ({ keepStep = false } = {}) => {
    try {
      const headers = {
        OtpCode: otpCode,
      };
      const minimumData = await getMinimumData(app_slug);
      setMinimumData(minimumData.data);
      const res = await getApplicationData(app_slug, { headers });
      setIsAuthenticated(true);
      setApplicationData(res.data);
      setUploadedFiles(res?.file_data ? res?.file_data : []);
      const appData = res.data;
      const vapeAttestationData = appData?.powerapp_addendum?.find(
        (addendum) => addendum.addendum === conditionalAddendums.VAPE_ATTESTATION,
      );
      const travelAddendumData = appData?.powerapp_addendum?.find(
        (addendum) => addendum.addendum === conditionalAddendums.TRAVEL_ADDENDUM,
      );
      const existingCbdFile = (res?.file_data || []).find(
        (file) => file.title === "CBD Product Pricing",
      );

      setActiveAppData(appData);

      if (isAppArchived(appData?.created_at) || res?.older_app) {
        setOpenArchiveApp(true);
        setIsArchivedApp(true);
        return;
      }

      const typeOfSales = appData?.powerappbusinessprofile?.type_of_sales;
      const originationOfTransaction =
        appData?.powerappbusinessprofile?.origination_of_transaction;

      if (appData?.pricing) {
        setPricingData(appData?.pricing);
      }
      if (appData?.powerappequipment) {
        setEquipmentData(appData?.powerappequipment);
      }
      setPricingModel(appData?.qualifiedtype);

      if (
        appData?.powerapp_finicity &&
        appData?.powerapp_finicity?.routing_num &&
        appData?.powerapp_finicity?.account_num &&
        String(appData.powerapp_finicity.routing_num).length > 0 &&
        String(appData.powerapp_finicity.account_num).length > 0
      ) {
        setHasFinicityRoutingAndAccountNumber(true);

        if (
          appData?.powerapp_finicity?.bank_name &&
          String(appData.powerapp_finicity.bank_name).length > 0
        ) {
          setIsFinicityConnected(true);
        }
      }

      setHasPlaidRoutingAndAccountNumber(false);
      setIsPlaidConnected(false);

      if (
        appData?.powerapp_plaid &&
        appData?.powerapp_plaid?.routing_num &&
        appData?.powerapp_plaid?.account_num &&
        String(appData.powerapp_plaid.routing_num).length > 0 &&
        String(appData.powerapp_plaid.account_num).length > 0
      ) {
        setHasPlaidRoutingAndAccountNumber(true);

        if (
          appData?.powerapp_plaid?.bank_name &&
          String(appData.powerapp_plaid.bank_name).length > 0
        ) {
          setIsPlaidConnected(true);
        }
      }

      const mappedFormData = {
        type_of_ownership: appData?.field_ownership || "",
        legal_business_name: appData?.field_legalname || "",
        dba_name: appData?.field_dba || "",
        tax_ein: appData?.field_ein || "",
        premise_type: appData?.premise_type || "",
        ownership_years: appData?.field_ownershipyears || "",
        ownership_months: appData.field_ownershipmonths || "",
        business_type: appData?.field_mcc || "",
        business_type_label: appData?.mcc_info?.description || "",
        payliance_industry: appData?.powerappbusinessprofile?.payliance_industry || "",
        tax_exempt: appData?.powerappbusinessprofile?.tax_exempt == "yes" ? "yes" : "no",
        business_phone: appData?.field_dbaphone || "",
        business_email: appData?.field_businessemail || "",
        business_website: appData?.field_website || "",
        dba_address: appData?.field_dbastreet || "",
        dba_city: appData?.field_dbacity || "",
        dba_state: appData?.field_dbastate || "",
        dba_zip: appData?.field_dbazip || "",
        location_years: appData?.field_locationyears || "",
        location_months: appData?.field_locationmonths || "",
        same_as_dba: appData?.same_as_legal == "yes" ? "yes" : "no",
        legal_address: appData?.field_legalstreet || "",
        legal_city: appData?.field_legalcity || "",
        legal_state: appData?.field_legalstate || "",
        legal_zip: appData?.field_legalzip || "",
        legal_phone: appData?.field_businessphone || "",

        // Personal Information
        title: appData?.title || "",
        personal_first_name: appData?.field_firstname || "",
        personal_last_name: appData?.field_lastname || "",
        personal_email: appData?.field_email || "",
        personal_phone: appData?.field_personalphone || "",
        field_personalstreet: appData?.field_personalstreet || "",
        field_personalcity: appData?.field_personalcity || "",
        field_personalstate: appData?.field_personalstate || "",
        field_personalzip: appData?.field_personalzip || "",
        field_residenceyears: appData?.field_residenceyears || "",
        field_residencemonths: appData?.field_residencemonths || "",
        field_documenttype: appData?.field_documenttype || "",
        field_documentnumber: appData?.field_documentnumber || "",
        field_driverstate: appData?.field_driverstate || "",
        document_country: appData?.document_country || "",
        field_birthdate: formatDate(appData?.field_birthdate) || "",
        field_ownershippercentage: appData?.field_ownershippercentage || "",
        field_socialsecurity: appData?.field_socialsecurity || "",
        personal_same_as_dba_legal: appData?.personal_same_as_dba_legal || "no",

        // Business Profile creditcardprocess
        thirdparty_involved_description:
          appData?.creditcardprocess?.third_party_identity || "",
        pci_complaint: appData?.creditcardprocess?.pci_complaint || "no",
        terminated_as_vmdax:
          appData?.creditcardprocess?.terminated_as_vmdax || "no",
        identified_visa_risk_program:
          appData?.creditcardprocess?.identified_visa_risk_program || "no",
        prior_data_compromise_investigation:
          appData?.creditcardprocess?.prior_data_compromise_invetigation ||
          "no",

        product_service_description:
          appData?.powerappbusinessprofile?.product_service_sold || "",
        prod_advertise_description: appData?.powerappbusinessprofile
          ?.prod_advertise_desc
          ? JSON.parse(appData?.powerappbusinessprofile?.prod_advertise_desc)
          : [],
        is_thirdparty_involved:
          appData?.powerappbusinessprofile?.thirdparty_delivered_radio || "no",
        visa_risk_program_desc:
          appData?.powerappbusinessprofile?.thirdparty_sales_per_desc || "",
        is_multiple_locations:
          appData?.powerappbusinessprofile?.is_multiple_locations || "no",
        multi_merchant: appData?.powerappbusinessprofile?.multi_merchant || "",
        multi_merchant_of:
          appData?.powerappbusinessprofile?.multi_merchant_of || "",
        additional_location:
          appData?.powerappbusinessprofile?.additional_location || "",
        additional_location_of:
          appData?.powerappbusinessprofile?.additional_location_of || "",

        dispute_recovery_program: appData?.dispute_recovery_program ?? true,

        // Credit Card Processing
        currently_accept_vdmax:
          appData?.creditcardprocess?.currently_processing || "no",
        cc_processing_locked: !!appData?.cc_processing_locked,
        ways_accepting_cards: appData?.creditcardprocess?.takingcards
          ? appData?.creditcardprocess?.takingcards
          : [],

        // internet: typeOfSales?.internet || "",
        on_premise_sale: typeOfSales?.on_premise_sale || "",
        off_premise_sale: typeOfSales?.off_premise_sale || "",
        telephone_order: typeOfSales?.telephone_order || "",
        mail_order: typeOfSales?.mail_order || "",

        sales_swiped: originationOfTransaction?.sales_swiped || "",
        sales_keyed: originationOfTransaction?.sales_keyed || "",
        internet: originationOfTransaction?.internet || "",
        recurring_bill_triel_radio:
          appData?.powerappbusinessprofile?.recurring_bill_triel_radio || "no",

        monthly_max_sales: appData?.bankinfo?.maxsales || "",
        avg_sale_amount: appData?.bankinfo?.ticketaverage || "",
        high_sale_amount: appData?.bankinfo?.tickethighest || "",

        bank_routing:
          (appData?.powerapp_plaid && appData?.powerapp_plaid?.routing_num) ||
          (appData?.powerapp_finicity &&
            appData?.powerapp_finicity?.routing_num) ||
          appData?.bankinfo?.bankrouting ||
          "",
        bank_account:
          (appData?.powerapp_plaid && appData?.powerapp_plaid?.account_num) ||
          (appData?.powerapp_finicity &&
            appData?.powerapp_finicity?.account_num) ||
          appData?.bankinfo?.bankaccount ||
          "",
        bank_name:
          (appData?.powerapp_plaid && appData?.powerapp_plaid?.bank_name) ||
          (appData?.powerapp_finicity &&
            appData?.powerapp_finicity?.bank_name) ||
          appData?.bankinfo?.bankname ||
          "",
        fraud_chargeback_tool:
          appData?.creditcardprocess?.fraud_prevention || false,
        cheque_processing:
          appData?.powerappbusinessprofile?.cheque_processing || false,
        business_funding_required:
          appData?.powerappbusinessprofile?.business_funding_required || false,

        // Equipment
        ems_boot_equipments: appData?.ems_boot_equipments || [],

        // Signers
        additional_owners: mapSigners(appData?.signers) || [],

        // Conditional Addendums
        cbd_addendum_file: existingCbdFile || null,
        ...vapeAttestationData?.data,
        ...travelAddendumData?.data,
      };

      if (keepStep) {
        // Step already set by the caller
      } else if (!minimumData?.data) {
        setStep(0);
      } else if (!user) {
        // Resolved in an effect: this closure's `sections` predates activeAppData
        setGuestStepSource({
          mappedFormData,
          minimumData: minimumData.data,
          progressionId: appData.lead_progression_id,
        });
      } else {
        setSignerStep(minimumData.data, appData.lead_progression_id);
      }

      setFormData((prev) => ({ ...prev, ...mappedFormData }));
    } catch (error) {
      console.log("Error fetching application data:", error);
      if (error?.status === 401) {
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const mapSigners = (signers) => {
    return signers.map((signer) => ({
      signer_first_name: signer.first_name,
      signer_last_name: signer.last_name,
      signer_email: signer.email,
      signer_phone: signer.phone,
      signer_address: signer.address_street,
      signer_city: signer.address_city,
      signer_state: signer.address_state,
      signer_zip: signer.address_zip_code,
      signer_country: signer.document_country,
      signer_social_security: signer.social_security,
      signer_drivers_license_number: signer.drivers_license_number,
      signer_drivers_license_state: signer.drivers_license_state,
      signer_date_of_birth: formatDate(signer.date_of_birth),
      signer_document_type: signer.document_type,
      signer_document_country: signer.document_country,
      signer_ownership_percentage: signer.ownership_percentage,
      signer_residence_years: signer.residence_years,
      signer_residence_months: signer.residence_months,
      signer_title: signer.title,
    }));
  };

  useEffect(() => {
    if (app_slug) {
      fetchApplicationData();
      getCountries().then((res) => {
        setCountryList(res.data);
      });
    }
    fetchMccLowRisk();
  }, [app_slug]);

  useEffect(() => {
    fetchApplicationData();
  }, [isAuthenticated]);

  const sections = {
    0: {
      id: "business-information",
      label: "Business Information",
      step: 0,
      validationSchema: hasPaylianceProcessor
        ? businessInformationSchema.concat(paylianceIndustrySchema)
        : businessInformationSchema,
    },
    1: {
      id: "personal-information",
      label: "Personal Information",
      step: 1,
      validationSchema: personalInformationSchema,
    },
    2: {
      id: "business-profile",
      label: "Business Profile",
      step: 2,
      validationSchema: businessProfileSchema,
    },
    3: {
      id: "credit-card-processing",
      label: "Credit Card Processing",
      step: 3,
      validationSchema: creditCardProcessingSchema.concat(
        Yup.object().shape({
          ...(cbdAddendumRequired ? cbdAddendumSchema.fields : {}),
          ...(vapeAttestationRequired ? vapeAttestationSchema.fields : {}),
          ...(travelAddendumRequired ? travelAddendumSchema.fields : {}),
        }),
      ),
    },
    7: {
      id: "review-submit",
      label: "Review & Submit",
      step: 7,
      validationSchema: hasPaylianceProcessor
        ? formSchema.concat(paylianceIndustrySchema)
        : formSchema,
    },
  };

  const stepSchema = useMemo(() => {
    const schema = sections[step]?.validationSchema;
    return user ? skipBlankFields(schema) : schema;
  }, [
    step,
    user,
    hasPaylianceProcessor,
    cbdAddendumRequired,
    vapeAttestationRequired,
    travelAddendumRequired,
  ]);

  const validateSteps = (data) =>
    Object.fromEntries(
      COMPLETION_STEPS.map((i) => {
        try {
          sections[i]?.validationSchema?.validateSync(data, {
            abortEarly: false,
          });
          return [i, true];
        } catch {
          return [i, false];
        }
      }),
    );

  const firstIncomplete = (completion) =>
    COMPLETION_STEPS.find((i) => !completion[i]) ?? null;

  /**
   * The step to post to salesAppPost. A step past 3 tells the server steps 0-3
   * are finished, and it acts on that: completion email, PDFs, progression. An
   * agent can save with gaps, so hold it at 3 until every step validates.
   */
  const serverStep = (requestedStep, values) =>
    requestedStep > 3 && firstIncomplete(validateSteps(values)) !== null
      ? 3
      : requestedStep;

  /**
   * Which of steps 0-3 validate, from the last-saved data (handleStepSubmit
   * refetches after every save). Never uses the agent's lenient schema:
   * completeness is a property of the application, not of who is looking. Step
   * 7 is excluded — its formSchema is a superset of steps 0-3.
   */
  const { stepCompletion, incompleteStep } = useMemo(() => {
    const completion = validateSteps(formData);
    return {
      stepCompletion: completion,
      incompleteStep: firstIncomplete(completion),
    };
  }, [
    formData,
    hasPaylianceProcessor,
    cbdAddendumRequired,
    vapeAttestationRequired,
    travelAddendumRequired,
  ]);

  const resolveGuestInitialStep = (
    mappedFormData,
    minimumData,
    progressionId,
  ) => {
    const currentSigner =
      minimumData?.signers?.length > 1
        ? minimumData.signers[signerType === "First" ? 0 : 1]
        : null;

    if (
      progressionId >= LeadProgression.APP_SIGNED ||
      currentSigner?.status === "signed"
    ) {
      setSignerStep(minimumData, progressionId);
      return;
    }

    const pendingStep = firstIncomplete(validateSteps(mappedFormData));
    if (pendingStep !== null) {
      setStep(pendingStep);
      return;
    }

    setSignerStep(minimumData, progressionId);
  };

  useEffect(() => {
    if (!guestStepSource) return;
    resolveGuestInitialStep(
      guestStepSource.mappedFormData,
      guestStepSource.minimumData,
      guestStepSource.progressionId,
    );
    setGuestStepSource(null);
  }, [guestStepSource]);

  const canSignInPerson =
    incompleteStep === null && activeAppData?.prices?.length > 0;

  /**
   * Handles section change in the application form
   * @param {number} `step` number - ID of the section to change to
   */
  const handleSectionChange = (step) => {
    setStep(step);
  };

  /**
   * Gets section-specific data from form values
   * @param {Object} values - Form values
   * @returns {Object} Section-specific data
   */
  const getSectionData = (values) => {
    const baseData = {
      editslug: app_slug,
      step: serverStep(step + 1, values),
      agent_url: match.params.agent_url,
      // Business Information
      type_of_ownership: values.type_of_ownership,
      legal_business_name: values.legal_business_name,
      dba_name: values.dba_name,
      tax_ein: values.tax_ein,
      premise_type: values.premise_type,
      ownership_years: values.ownership_years,
      ownership_months: values.ownership_months,
      business_type: values.business_type,
      payliance_industry: values.payliance_industry,
      tax_exempt: values.tax_exempt,
      location_years: values.location_years,
      location_months: values.location_months,
      same_as_legal: values.same_as_dba === "yes" ? "yes" : "no",
      business_phone: values.business_phone,
      business_email: values.business_email,
      business_website: values.business_website,
      // Add explicit mappings for DBA fields to match backend expectations
      dba_address: values.dba_address,
      dba_city: values.dba_city,
      dba_state: values.dba_state,
      dba_zip: values.dba_zip,
      // Legal address fields based on same_as_dba logic
      legal_address:
        values.same_as_dba === "yes"
          ? values.dba_address
          : values.legal_address,
      legal_city:
        values.same_as_dba === "yes" ? values.dba_city : values.legal_city,
      legal_state:
        values.same_as_dba === "yes" ? values.dba_state : values.legal_state,
      legal_zip:
        values.same_as_dba === "yes" ? values.dba_zip : values.legal_zip,
      legal_phone:
        values.same_as_dba === "yes"
          ? values.business_phone
          : values.legal_phone,
      // Business information fields

      // Personal information field mappings
      title: values.title,
      personal_same_as_dba_legal:
        values.personal_same_as_dba_legal === "no"
          ? ""
          : values.personal_same_as_dba_legal,
      personal_first_name: values.personal_first_name,
      personal_last_name: values.personal_last_name,
      personal_email: values.personal_email,
      personal_phone: values.personal_phone,
      field_personalstreet: values.field_personalstreet,
      field_personalcity: values.field_personalcity,
      field_personalstate: values.field_personalstate,
      field_personalzip: values.field_personalzip,
      field_residenceyears: values.field_residenceyears,
      field_residencemonths: values.field_residencemonths,
      field_documenttype: values.field_documenttype,
      field_documentnumber: values.field_documentnumber,
      document_country: values.document_country,
      field_driverstate: values.field_driverstate,
      field_birthdate: values.field_birthdate,
      field_ownershippercentage: values.field_ownershippercentage,
      field_socialsecurity: values.field_socialsecurity,
      signers: values.additional_owners,

      // Business Profile field mappings
      product_service_description: values.product_service_description,
      prod_advertise_description: values.prod_advertise_description,
      is_thirdparty_involved: values.is_thirdparty_involved,
      thirdparty_involved_description: values.thirdparty_involved_description,
      pci_complaint: values.pci_complaint,
      terminated_as_vmdax: values.terminated_as_vmdax,
      prior_data_compromise_investigation:
        values.prior_data_compromise_investigation,
      identified_visa_risk_program: values.identified_visa_risk_program,
      visa_risk_program_desc: values.visa_risk_program_desc,
      is_multiple_locations: values.is_multiple_locations,
      multi_merchant: values.multi_merchant,
      multi_merchant_of: values.multi_merchant_of,
      additional_location: values.additional_location,
      additional_location_of: values.additional_location_of,
      // backend ignores this for non-entity offices
      dispute_recovery_program: values.dispute_recovery_program ? 1 : 0,

      // Credit Card Processing field mappings
      currently_accept_vdmax: values.currently_accept_vdmax,
      // Combine card acceptance methods into an array for the backend
      ways_accepting_cards: values.ways_accepting_cards,
      // Sales percentages
      origination_of_transaction: {
        sales_swiped: values.sales_swiped,
        sales_keyed: values.sales_keyed,
        internet: values.internet,
      },
      recurring_bill_triel_radio: values.recurring_bill_triel_radio,
      // Monthly sales amounts with proper field names
      monthly_max_sales: values.monthly_max_sales,
      avg_sale_amount: values.avg_sale_amount,
      high_sale_amount: values.high_sale_amount,
      // Bank verification options - use the correct data type for each field
      bank_routing: values?.bank_routing?.replace(/-/g, ""),
      bank_account: values.bank_account,
      bank_name: values.bank_name,

      fraud_chargeback_tool: values.fraud_chargeback_tool ? "yes" : "no", // Expects string
      fraud_prevention: values.fraud_chargeback_tool ? "yes" : "no", // Expects string in CreditCardProcess model
      cheque_processing: values.accepting_e_cheks ? "yes" : "no", // Send 'yes'/'no' instead of 1/0
      business_funding_required: values.business_funding ? 1 : 0, // Expects integer in BusinessProfile model
      ...(user &&
        step === 3 && {
          cc_processing_locked: values.cc_processing_locked,
        }),
    };

    return baseData;
  };

  const errorsToTouched = (errors) => {
    if (typeof errors !== "object" || errors === null) {
      return true;
    }
    if (Array.isArray(errors)) {
      return errors.map((item) => errorsToTouched(item));
    }
    return Object.keys(errors).reduce((acc, key) => {
      acc[key] = errorsToTouched(errors[key]);
      return acc;
    }, {});
  };

  const handleFormSubmit = async (e, formikProps) => {
    e.preventDefault();

    //Note: Revisit this later as it clears EMS API errors
    const errors = await formikProps.validateForm();
    if (errors && Object.keys(errors).length > 0) {
      formikProps.setTouched(errorsToTouched(errors));
      scrollToError(errors);
      return;
    }

    await handleStepSubmit(formikProps.values, formikProps);
  };

  const handleStepSubmit = async (values, formikHelpers) => {
    console.log("handleSubmit called with values:", values);
    console.log("handleSubmit called with formikHelpers:", formikHelpers);
    try {
      setIsSubmitting(true);

      if (step === 0) {
        try {
          const [businessPhoneValid, legalPhoneValid] = await Promise.all([
            validatePhone(
              values.business_phone,
              "business_phone",
              formikHelpers.setFieldError,
              formikHelpers.setFieldTouched,
            ),
            validatePhone(
              values.legal_phone,
              "legal_phone",
              formikHelpers.setFieldError,
              formikHelpers.setFieldTouched,
            ),
          ]);

          if (!businessPhoneValid || !legalPhoneValid) {
            const phoneErrors = {};
            if (!businessPhoneValid) {
              phoneErrors.business_phone = "* Enter a valid phone";
            }
            if (!legalPhoneValid) {
              phoneErrors.legal_phone = "* Enter a valid phone";
            }
            scrollToError({ ...formikHelpers.errors, ...phoneErrors });
            setIsSubmitting(false);
            return;
          }
        } catch (error) {
          console.error("Phone validation error:", error);
          toast.error("Error validating phone numbers");
          scrollToError(formikHelpers.errors);
          setIsSubmitting(false);
          return;
        }
      }

      if (step === 1) {
        try {
          const [phoneValid, drivingLicenseValid] = await Promise.all([
            validatePhone(
              values.personal_phone,
              "personal_phone",
              formikHelpers.setFieldError,
              formikHelpers.setFieldTouched,
            ),
            validateDrivingLicense(
              values.field_documentnumber,
              values.field_driverstate,
              values.field_documenttype,
              "field_documentnumber",
              formikHelpers.setFieldError,
              formikHelpers.setFieldTouched,
            ),
          ]);

          if (!phoneValid || !drivingLicenseValid) {
            const phoneErrors = {};
            if (!phoneValid) {
              phoneErrors.personal_phone = "* Enter a valid phone";
            }
            if (!drivingLicenseValid) {
              phoneErrors.field_documentnumber = "* Enter a valid ID number";
            }
            scrollToError({ ...formikHelpers.errors, ...phoneErrors });
            toast.error("Please fix validation errors before proceeding");
            setIsSubmitting(false);
            return;
          }

          if (values.additional_owners && values.additional_owners.length > 0) {
            const additionalOwnerValidations = [];

            values.additional_owners.forEach((owner, index) => {
              if (owner.signer_phone) {
                additionalOwnerValidations.push({
                  fieldPath: `additional_owners.${index}.signer_phone`,
                  errorMessage: "* Enter a valid phone",
                  promise: validatePhone(
                    owner.signer_phone,
                    `additional_owners.${index}.signer_phone`,
                    formikHelpers.setFieldError,
                    formikHelpers.setFieldTouched,
                  ),
                });
              }

              if (
                owner.signer_drivers_license_number &&
                owner.signer_drivers_license_state
              ) {
                additionalOwnerValidations.push({
                  fieldPath: `additional_owners.${index}.signer_drivers_license_number`,
                  errorMessage: "* Enter a valid ID number",
                  promise: validateDrivingLicense(
                    owner.signer_drivers_license_number,
                    owner.signer_drivers_license_state,
                    owner.signer_document_type,
                    `additional_owners.${index}.signer_drivers_license_number`,
                    formikHelpers.setFieldError,
                    formikHelpers.setFieldTouched,
                  ),
                });
              }
            });

            if (additionalOwnerValidations.length > 0) {
              const additionalOwnerResults = await Promise.all(
                additionalOwnerValidations.map((v) => v.promise),
              );

              const additionalOwnerErrors = {};
              additionalOwnerValidations.forEach((validation, idx) => {
                if (additionalOwnerResults[idx] !== true) {
                  additionalOwnerErrors[validation.fieldPath] =
                    validation.errorMessage;
                }
              });

              if (Object.keys(additionalOwnerErrors).length > 0) {
                scrollToError({
                  ...formikHelpers.errors,
                  ...additionalOwnerErrors,
                });
                toast.error(
                  "Please fix validation errors for additional owners before proceeding",
                );
                setIsSubmitting(false);
                return;
              }
            }
          }
        } catch (error) {
          console.error("Validation error:", error);
          toast.error("Error validating form data");
          scrollToError(formikHelpers.errors);
          setIsSubmitting(false);
          return;
        }
      }

      if (
        step === 3 &&
        !hasFinicityRoutingAndAccountNumber &&
        !hasPlaidRoutingAndAccountNumber
      ) {
        try {
          const result = await validateRoutingNumber(
            values.bank_routing,
            formikHelpers.setFieldError,
          );

          if (!result || !result?.status) {
            const routingNumberError = {};
            if (!result || !result?.status) {
              routingNumberError.bank_routing =
                "* Enter a valid routing number";
            }
            scrollToError({ ...formikHelpers.errors, ...routingNumberError });
            toast.error("Please fix validation errors before proceeding");
            setIsSubmitting(false);
            return;
          }
        } catch (error) {
          console.error("Validation error:", error);
          toast.error("Error validating form data");
          scrollToError(formikHelpers.errors);
          setIsSubmitting(false);
          return;
        }
      }

      if (Object.keys(formikHelpers.errors).length > 0) {
        scrollToError(formikHelpers.errors);
        setIsSubmitting(false);
        return;
      }

      if (step === 3 && isFirstTimeAppOpenedWithMagicLink) {
        // setIsFirstTimeAppOpenedWithMagicLink(false);
        // fetchApplicationData();
        toast.success(
          "All steps have been completed successfully! To sign the application, please verify your identity using the OTP — you’ll now be redirected to the OTP page.",
        );
        setTimeout(() => {
          window.location.reload();
        }, 5000);
      }

      if (step === 3 && cbdAddendumRequired && values.cbd_addendum_file instanceof File) {
        const form = new FormData();
        form.append("editslug", app_slug);
        form.append("cbd_addendum_file", values.cbd_addendum_file);
        const cbdRes = await saveCbdAddendum(form, { headers: { OtpCode: otpCode } });
        if (!cbdRes?.status) {
          toast.error(cbdRes?.message || "Failed to upload CBD product pricing");
          setIsSubmitting(false);
          return;
        }
      }

      if (step === 3 && vapeAttestationRequired) {
        const vapeRes = await saveVapeAttestationAddendum(
          {
            editslug: app_slug,
            data: {
              vape_attestation_what_is_your_source_of_marketing: values.vape_attestation_what_is_your_source_of_marketing,
              vape_attestation_what_audience_is_the_product_marketed_to: values.vape_attestation_what_audience_is_the_product_marketed_to,
              vape_attestation_product_1: values.vape_attestation_product_1,
              vape_attestation_product_2: values.vape_attestation_product_2,
              vape_attestation_product_3: values.vape_attestation_product_3,
              vape_attestation_product_4: values.vape_attestation_product_4,
              vape_attestation_product_5: values.vape_attestation_product_5,
              where_are_the_products_manufactured: values.where_are_the_products_manufactured,
              vape_attestation_product_types: values.vape_attestation_product_types,
              vape_attestation_method_of_age_verification: values.vape_attestation_method_of_age_verification,
              vape_attestation_other_method_of_age_verification: values.vape_attestation_other_method_of_age_verification,
            },
          },
          { headers: { OtpCode: otpCode } },
        );
        if (!vapeRes?.status) {
          toast.error(vapeRes?.message || "Failed to save vape attestation");
          setIsSubmitting(false);
          return;
        }
      }

      if (step === 3 && travelAddendumRequired) {
        const travelFieldKeys = [
          "travel_business_activities",
          "travel_other_business_activity",
          "travel_sales_third_party_packages",
          "travel_agency_accommodation_only",
          "travel_agency_non_flight_transport",
          "travel_flight_own_merchant",
          "travel_flight_airline_merchant",
          "travel_other_sales_agent",
          "travel_other_sales_description",
          "travel_total_gross_turnover",
          "travel_airline_suppliers",
          "travel_total_spend_travel",
          "travel_hotel_suppliers",
          "travel_total_spend_accommodation",
          "travel_local_services",
          "travel_total_spend_other",
          "travel_countries",
          ...Object.keys(getTravelSeasonalityFields()),
          "travel_average_deposit_percentage",
          "travel_average_time_deposit_travel",
          "travel_average_time_balance_travel",
          "travel_delivery_1_7_days",
          "travel_delivery_8_14_days",
          "travel_delivery_15_30_days",
          "travel_delivery_31_60_days",
          "travel_delivery_61_90_days",
          "travel_delivery_91_120_days",
          "travel_delivery_121_150_days",
          "travel_delivery_151_180_days",
          "travel_delivery_over_180_days",
          "travel_consumer_protection",
          "travel_website_address",
        ];
        const travelRes = await saveTravelAddendum(
          {
            editslug: app_slug,
            addendum: conditionalAddendums.TRAVEL_ADDENDUM,
            data: Object.fromEntries(travelFieldKeys.map((k) => [k, values[k]])),
          },
          { headers: { OtpCode: otpCode } },
        );
        if (!travelRes?.status) {
          toast.error(travelRes?.message || "Failed to save travel addendum");
          setIsSubmitting(false);
          return;
        }
      }

      // Prepare data for submission
      const sectionData = getSectionData(values);
      const transformedValues = {
        ...sectionData,
      };

      // Submit to API
      const res = await salesAppPost(transformedValues, { headers: { OtpCode: otpCode } });
      if (res.status) {
        // Handle successful submission
        const data = res.data;
        if (!app_slug) {
          history.push(`${history.location.pathname}/${data.editslug}`);
          setEditSlug(data.editslug);
          app_slug = data.editslug;
        }

        formikHelpers.setTouched({});
        // An agent can leave gaps behind a lead_progression_id they already
        // advanced, so routing a guest by it lands them on review or thank-you
        // until the refetch below sends them back. Send them to the next step
        // that still has gaps instead, which is where that refetch would put
        // them anyway.
        const nextIncompleteStep = user
          ? null
          : firstIncomplete(validateSteps(values));
        // A held-back step leaves lead_progression_id at 3; the agent still moves on to review
        const heldBack = transformedValues.step < step + 1;
        setStep(nextIncompleteStep ?? (heldBack ? 7 : data.lead_progression_id));
        toast.success("Data saved successfully");
        document?.querySelector("[data-scroll-container]")?.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });

        fetchApplicationData({ keepStep: heldBack });
      } else {
        // Handle API error response
        toast.error(res.data.message || "Error saving data");
      }
    } catch (error) {
      // Handle unexpected errors
      console.error(error);
      toast.error(
        error.response?.data?.message || "An error occurred while saving",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles save and exit action
   */
  const handleSaveAndExit = async (formikHelpers, values) => {
    try {
      toast.dismiss();
      setIsSubmitting(true);
      const loadingToast = toast.loading("Saving application...");

      const sectionData = getSectionData(values);
      const transformedValues = {
        ...sectionData,
        editslug: match?.params?.editslug || app_slug,
        step: serverStep(step, values),
        agent_url: match?.params?.agent_url,
      };

      const errors = await formikHelpers.validateForm();
      const erroredFields = Object.keys(errors);

      if (erroredFields.length > 0) {
        formikHelpers.setTouched(
          erroredFields.reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {}),
        );
        toast.dismiss(loadingToast);
        toast.error("Please fix all errors before proceeding");
        return;
      }

      const response = await salesAppPost(transformedValues, { headers: { OtpCode: otpCode } });
      toast.dismiss(loadingToast);

      if (response.status) {
        if (!app_slug && response.data?.editslug) {
          setEditSlug(response.data.editslug);
        }
        toast.success("Application saved successfully");
        history.push("/applications");
      } else {
        toast.error(response.message || "Failed to save application");
      }
    } catch (error) {
      console.error("Error saving application:", error);
      toast.error("Failed to save application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Opens the in-person signing modal. Gated by canSignInPerson at both call sites. */
  const handleSignInPerson = () => {
    setIsSendToMerchantModalOpen(false);
    setIsSignInPersonModalOpen(true);
  };

  /**
   * Handles send to merchant action - shows confirmation dialog
   */
  const handleSendToMerchant = () => {
    if (!app_slug) {
      toast.error("Application not found. Please save the application first.");
      return;
    }

    // The merchant fills what the agent left blank, but they cannot supply the
    // commercial terms — the agreement they sign must carry pricing.
    if (!(activeAppData?.prices?.length > 0)) {
      toast.error("Add pricing before sending this application for signature.");
      return;
    }

    setShowSendToMerchantConfirm(true);
  };

  /**
   * Actually sends the application link to merchant after confirmation
   */
  const confirmSendToMerchant = async () => {
    setShowSendToMerchantConfirm(false);
    setSendingLinkToMerchant(true);

    try {
      const res = await sendApplicationLinkToMerchant(app_slug);
      if (res?.status) {
        setIsSendToMerchantModalOpen(true);
      } else {
        toast.error(
          res?.message || "Failed to send application link to merchant",
        );
      }
    } catch (err) {
      console.error("Error sending application link to merchant", err);
      toast.error("Error sending application link to merchant");
    } finally {
      setSendingLinkToMerchant(false);
    }
  };

  /**
   * Handles view applications action
   */
  const handleViewApplications = () => {
    setIsSendToMerchantModalOpen(false);
    history.push("/applications");
  };

  const isPricingEquipmentAttached = () => {
    return (
      activeAppData?.powerappequipment &&
      activeAppData?.powerappequipment.length > 0 &&
      activeAppData?.prices &&
      activeAppData?.prices.length > 0
    );
  };
  /**
   * Renders the content for the current section
   * @returns {React.ReactNode} Section content
   */
  const renderSectionContent = () => {
    switch (step) {
      case 0:
        return <BusinessInformation />;
      case 1:
        return <PersonalInformation />;
      case 2:
        return <BusinessProfile disputeRecoveryVisible={disputeRecoveryVisible} />;
      case 3:
        return (
          <>
            <CreditCardProcessing />
            {cbdAddendumRequired && <CbdAddendum isKurvDomain={isKurvDomain} />}
            {vapeAttestationRequired && <VapeAttestation />}
            {travelAddendumRequired && <TravelAddendum />}
          </>
        );
      case 5:
      case 6:
      case 7:
        return (
          <ReviewSubmit
            onSectionChange={handleSectionChange}
            pricingModel={pricingModel}
            pricingData={pricingData}
            equipmentData={equipmentData}
            activeAppData={activeAppData}
            reRender={fetchApplicationData}
            incompleteStep={incompleteStep}
            stepCompletion={stepCompletion}
          />
        );
      default:
        return <BusinessInformation />;
    }
  };

  // Effect to hide breadcrumbs when showing ThankYou component
  useEffect(() => {
    if (activeAppData && activeAppData.lead_progression_id === 8) {
      onHideBreadcrumbs && onHideBreadcrumbs(true);
    } else {
      onHideBreadcrumbs && onHideBreadcrumbs(false);
    }
  }, [activeAppData, onHideBreadcrumbs]);

  return (
    <div
      className={`${styles.container} ${isKurvDomain ? styles.kurv_theme : ""}`}
    >
      {loading && (
        <div className={styles.loadingContainer}>
          <Spinner />
        </div>
      )}

      {!loading && !isArchivedApp && (
        <>
          {/* Anonymous user verification */}
          {!isAuthenticated && !isFirstTimeAppOpenedWithMagicLink && (
            <div>
              {!isOtpSent && app_slug && (
                <SendOtp data={minimumData} editSlug={app_slug} />
              )}

              {isOtpSent && (
                <VerifyOtp data={minimumData} editSlug={app_slug} />
              )}
            </div>
          )}
          {/* end Anonymous user verification  */}
          {(step == 6 || step == 5) && !user ? (
            <>
              <ThankYou
                appData={activeAppData}
                appSlug={app_slug}
                reRender={fetchApplicationData}
                minimumData={minimumData}
              />
            </>
          ) : step <= 7 ? (
            <>
              <div className={styles.content}>
                <Formik
                  enableReinitialize
                  initialValues={formData}
                  validationSchema={stepSchema}
                  onSubmit={handleStepSubmit}
                  validateOnChange={true}
                  validateOnBlur={true}
                >
                  {(formikProps) => (
                    <form
                      className={styles.form}
                      onSubmit={(e) => handleFormSubmit(e, formikProps)}
                    >
                      {/* {console.log("Values", formikProps.values)} */}
                      {console.log("Errors", formikProps.errors)}
                      {console.log("Touched", formikProps.touched)}

                      {(isAuthenticated ||
                        isFirstTimeAppOpenedWithMagicLink) && (
                        <ApplicationStepperMobile
                          sections={Object.values(sections).map((section) => ({
                            ...section,
                            completed: stepCompletion[section.step],
                          }))}
                          onSectionChange={handleSectionChange}
                          step={step}
                          isKurvDomain={isKurvDomain}
                        />
                      )}

                      <div className={styles.formSection}>
                        {renderSectionContent()}
                        <div className={styles.formActions}>
                          <div
                            className={`${styles.buttonsContainer} ${step === 7 ? styles.reviewSubmit : ""}`}
                          >
                            {user ? (
                              <>
                                {step === 7 ? (
                                  <>
                                    {otpVerificationTime === null && (
                                      <div className={styles.formSubmitButtons}>
                                        <ButtonIcon
                                          onClick={() =>
                                            handleSaveAndExit(
                                              formikProps,
                                              formikProps.values,
                                            )
                                          }
                                          type="button"
                                          size="large"
                                          bgColor="#EDEDF4"
                                          textColor="#050506"
                                        >
                                          Save
                                        </ButtonIcon>
                                        <ButtonIcon
                                          onClick={handleSignInPerson}
                                          disabled={!canSignInPerson}
                                          title={
                                            canSignInPerson
                                              ? undefined
                                              : "Complete the required fields and add pricing before signing in person"
                                          }
                                          bgColor="#EDEDF4"
                                          textColor="#050506"
                                          type="button"
                                          size="large"
                                        >
                                          Sign in Person
                                        </ButtonIcon>
                                        <ButtonIcon
                                          onClick={() => handleSendToMerchant()}
                                          type="button"
                                          size="large"
                                          bgColor={
                                            isKurvDomain ? "#22A276" : "#4c70ef"
                                          }
                                          textColor="#fff"
                                          isLoading={sendingLinkToMerchant}
                                        >
                                          Send to Merchant
                                        </ButtonIcon>
                                      </div>
                                    )}
                                  </>
                                ) : step !== 6 ? (
                                  <div className={styles.formSubmitButtons}>
                                    <ButtonIcon
                                      onClick={() =>
                                        handleSaveAndExit(
                                          formikProps,
                                          formikProps.values,
                                        )
                                      }
                                      type="button"
                                      size="large"
                                      textColor="#254FE2"
                                    >
                                      Save
                                    </ButtonIcon>
                                    <ButtonIcon
                                      onClick={() => handleSendToMerchant()}
                                      type="button"
                                      size="large"
                                      bgColor="#EDEDF4"
                                      textColor="#050506"
                                      isLoading={sendingLinkToMerchant}
                                    >
                                      Send to Merchant
                                    </ButtonIcon>

                                    <ButtonIcon
                                      type="submit"
                                      size="large"
                                      bgColor={
                                        isKurvDomain ? "#22A276" : "#4c70ef"
                                      }
                                      textColor="#fff"
                                      isLoading={isSubmitting}
                                    >
                                      Save and next
                                    </ButtonIcon>
                                  </div>
                                ) : (
                                  ""
                                )}
                              </>
                            ) : step <= 3 ? (
                              <div className={styles.formSubmitButtons}>
                                <ButtonIcon
                                  type="submit"
                                  bgColor={isKurvDomain ? "#22A276" : "#4c70ef"}
                                  textColor="#fff"
                                  isLoading={isSubmitting}
                                  size="large"
                                >
                                  Save and next
                                </ButtonIcon>
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                        </div>
                      </div>
                      <div className={styles.right}>
                        <ApplicationStepper
                          sections={Object.values(sections).map((section) => ({
                            ...section,
                            completed: stepCompletion[section.step],
                          }))}
                          onSectionChange={handleSectionChange}
                          step={step}
                          isKurvDomain={isKurvDomain}
                        />
                        <div className={styles.fileUploadContainer}>
                          <FileUpload isKurvDomain={isKurvDomain} />
                        </div>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>

              <SignInPersonModal
                isOpen={isSignInPersonModalOpen}
                onClose={() => setIsSignInPersonModalOpen(false)}
                minimumData={minimumData}
                reRender={fetchApplicationData}
              />

              <ConfirmationModal
                open={showSendToMerchantConfirm}
                onClose={() => setShowSendToMerchantConfirm(false)}
                message="Are you sure you want to send the application link to the merchant?"
                onConfirmClick={confirmSendToMerchant}
                submitButtonText="Send"
                confirmButtonColor="blue"
              />

              <SendToMerchantModal
                isOpen={isSendToMerchantModalOpen}
                onClose={() => setIsSendToMerchantModalOpen(false)}
                merchantLink={window.location.href}
                onContinue={handleSignInPerson}
                onViewApplications={handleViewApplications}
                canContinueInPerson={canSignInPerson}
              />
            </>
          ) : (
            <>
              <ThankYou
                appData={activeAppData}
                appSlug={app_slug}
                reRender={fetchApplicationData}
                minimumData={minimumData}
              />
            </>
          )}
        </>
      )}
      {/* Archive popup */}
      {openArchiveApp && (
        <ArchiveApp
          open={openArchiveApp}
          onClose={handleArchiveClose}
          isAnonymousUser={!user}
        />
      )}
    </div>
  );
};

export default withRouter(ApplicationForm);
